//IGifted.jsx
import React, { useCallback, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import { useSelector } from 'react-redux';
import MainLayout from '../../layouts/MainLayout';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift, faEye } from '@fortawesome/free-solid-svg-icons';

const IGifted = () => {
    const authState = useSelector(state => state.authReducer);
    const gifterId = authState.user._id;
    const [fetchData, { loading }] = useFetch();

    const [iGifted, setIGifted] = useState([]);
    const [selectedIGiftedId, setSelectedIGiftedId] = useState('');

    //IGiftedList
    const fetchIGiftedList = useCallback(() => {
        const config = {
            url: `/giftcoupon/gifter/${gifterId}`,
            method: "get",
            headers: { Authorization: authState.token }
        };

        fetchData(config, { showSuccessToast: false })
            .then(response => {
                if (response && Array.isArray(response.giftCoupons)) {
                    setIGifted(response.giftCoupons);
                }  else {
                    console.error("Invalid response format: expected array or object");
                }
            })
            .catch(error => {
                console.error("Error fetching gifted coupons:", error);
            });
        
    }, [authState.token, fetchData, gifterId]);

    useEffect(() => {
        fetchIGiftedList();
    }, [fetchIGiftedList]);

    const handleIGiftedDescView = (itemId) => {
        setSelectedIGiftedId(prevId => (prevId === itemId ? null : itemId));
    };

    const sortedIGifted = iGifted.sort((a, b) => {
        const dateA = new Date(a.dateCreated);
        const dateB = new Date(b.dateCreated);
        return dateB - dateA;
    });

    const formatDateTime = (date) => {
        const options = {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hour12: false
        };
        const jsDate = new Date(date);
        const day = jsDate.getDate().toString().padStart(2, '0');
        const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
        const year = jsDate.getFullYear();
        const hours = jsDate.getHours().toString().padStart(2, '0');
        const minutes = jsDate.getMinutes().toString().padStart(2, '0');
        const seconds = jsDate.getSeconds().toString().padStart(2, '0');
        return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    };

    return (
        <MainLayout>
            <div className="bg-gray-700 py-2">
                <h3 className='text-sm lg:text-lg font-bold text-center text-white mx-2 border-b border-gray-500'>
                    I Gifted
                </h3>
            </div>
            <div className="mx-auto px-4 py-2 max-w-screen-lg">
                <div className="border border-gray-600 rounded-lg shadow-lg p-2 bg-gray-800">
                    {loading ? (
                        <Loader />
                    ) : (
                        <div className='w-full'>
                            <h2 className="text-sm lg:text-lg font-semibold text-white bg-green-600 rounded-lg p-4 mb-6 flex items-center">
                                <FontAwesomeIcon icon={faGift} className="text-red-500 text-3xl mr-3" />
                                I Gifted
                            </h2>
                            <div>
                                {iGifted && iGifted.length === 0 ? (
                                    <div className='w-full h-64 flex items-center justify-center text-yellow-400'>
                                        No records found!
                                    </div>
                                ) : (
                                    sortedIGifted.map((igifted, index) => (
                                        <div key={igifted._id} className='bg-gray-900 my-3 p-2 text-gray-300 rounded-lg shadow-md transition-transform transform hover:scale-105'>
                                            <div className='flex items-center mb-2'>
                                                <span className='text-sm lg:text-lg font-medium text-white'>{index + 1}. {igifted.bookTitle}</span>
                                                <Tooltip text={"View details"} position={"top"}>
                                                    <span className='ml-auto text-blue-400 hover:text-blue-600 cursor-pointer' onClick={() => handleIGiftedDescView(igifted._id)}>
                                                        <FontAwesomeIcon icon={faEye} />
                                                    </span>
                                                </Tooltip>
                                            </div>
                                            <div className='text-gray-400'>
                                                <span className='text-sm lg:text-sm font-medium'>To: <span className='text-blue-400'>{igifted.giftFor}</span> on {formatDateTime(igifted.dateCreated)}</span>
                                            </div>
                                            {selectedIGiftedId === igifted._id && (
                                                <div className='bg-gray-800 text-gray-300 mt-2 p-2 rounded-lg border border-gray-600'>
                                                    <h2 className='text-blue-500 text-sm lg:text-lg font-semibold mb-2'>{igifted.bookTitle}</h2>
                                                    <p><strong>Is Redeemed:</strong> {igifted.isRedeemed ? "Yes" : "Not Yet"}</p>
                                                    <p><strong>Redeemed By:</strong> {igifted.isRedeemed ? igifted.giftedTo : "Not Redeemed Yet"}</p>
                                                    <p><strong>Gift For / To:</strong> <span className='text-purple-400'>{igifted.giftFor}</span></p>
                                                    <p><strong>Redeemed Date:</strong> {formatDateTime(igifted.dateRedeemed)}</p>
                                                    <p><strong>Thank You Message:</strong> <span className='text-yellow-400'>{igifted.giftThankYouMessage}</span></p>
                                                    <p><strong>Gift Voucher Number:</strong> <span className='text-purple-400'>{igifted.couponNumber}</span></p>
                                                    <p><strong>Gift Voucher Amount:</strong> {igifted.paymentAmount.toFixed(2)}</p>
                                                    <p><strong>Date Gifted:</strong> {new Date(igifted.dateCreated).toLocaleDateString()}</p>
                                                    <p><strong>Gifter (Your) Name:</strong> <span className='text-yellow-400'>{igifted.gifterName}</span></p>
                                                    <p><strong>Gifter (Your) Message:</strong> <span className='text-purple-400'>{igifted.giftMessage}</span></p>
                                                    <p><strong>Gifter (Your) Signature Line:</strong> <span className='text-yellow-400'>{igifted.gifterSignatureLine}</span></p>
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </MainLayout>
    );
};

export default IGifted;


